import React, { useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Formik, ErrorMessage } from 'formik';
import './styles.css';
import {CSSTransition} from 'react-transition-group';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicInput from '../../../../components/BasicInput';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSelect from '../../../../components/BasicSelect';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import CriteriaList from '../../../../components/CriteriaList';
import EmailRequirements from '../../../../components/EmailRequirements';
import FormContentLoader from '../../../../components/FormContentLoader';
import LoadMask from '../../../../components/LoadMask';
import PanelContainer from '../../../../components/PanelContainer';
import TextEditor from '../../../../components/TextEditor';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import FileHelper from '../../../../helpers/file';
import useApi from '../../../../hooks/useApi';
import {TYPE} from '../../../../pages/SetupEmail';
import BasicDialog from '../../../Dialog/components/BasicDialog';
import ConfirmDialog from '../../../Dialog/components/ConfirmDialog';
import FormDialog from '../../../Dialog/components/FormDialog';
import ResponseErrorDialog from '../../../Dialog/components/ResponseErrorDialog';
import AddSetupEmailRequirementsForm from '../AddSetupEmailRequirementsForm';
import SendTestEmailForm from '../SendTestEmailForm';
import ActiveSlideToggle from './components/ActiveSlideToggle';
import EmailAttachments from './components/EmailAttachments';
import {Normalizer} from './normalize';
import {ACTION, reducer} from './reducer';
import {SetupEmailFormValidationSchema} from './validate';

const initialState = {
  init: false,
  PositionTitle: '',
  fields: {
    ProcessEmailSetupID: null,
    RecruiterID: null,
    Active: 'No',
    Subject: '',
    Message: '',
  },
  options: {
    RecruiterID: [],
  },
  requirements: [],
};

const SetupEmailForm = props => {
  const { type, id, onInitError, onCloseForm } = props;

  const api = useApi();

  const [state, dispatch] = useReducer(reducer, initialState);

  const [hasActiveReq, idDeletable, testMailEnabled] = useMemo(
    () => [
      state.requirements &&
        !!state.requirements.length &&
        state.requirements.some(r => r.Active === 'Yes'),
      state.fields.ProcessEmailSetupID ||
        (state.requirements && !!state.requirements.length) ||
        (state.attachments && !!state.attachments.length),
      state.fields.ProcessEmailSetupID,
    ],
    [state]
  );

  const hint =
    type === 'project'
      ? 'You need to add at least 1 requirement, assign a Recruiter and enter Subject and Message in order to activate this feature for this project.'
      : 'You need to add at least 1 requirement, assign a Recruiter and enter Subject and Message in order to activate this feature for this process.';

  const [
    endpointGetForm,
    endpointSaveForm,
    endpointRemoveAll,
    endpointSaveAttach,
    endpointDownloadAttach,
    endpointRemoveAttach,
    endpointViewReq,
    endpointRemoveReq,
    endpointSendTestEmail,
  ] = useMemo(() => {
    return TYPE.PROCESS === type
      ? [
          ENDPOINT.FORM.GET.JOB_EMAIL_SETUP,
          ENDPOINT.FORM.SAVE.JOB_EMAIL_SETUP,
          ENDPOINT.ACTION.EMAIL_SETUP_JOB.REMOVE_ALL,
          ENDPOINT.ACTION.EMAIL_SETUP_JOB.SAVE_ATTACH,
          ENDPOINT.ACTION.EMAIL_SETUP_JOB.DOWNLOAD_ATTACH,
          ENDPOINT.ACTION.EMAIL_SETUP_JOB.REMOVE_ATTACH,
          ENDPOINT.ACTION.EMAIL_SETUP_JOB.VIEW_REQ,
          ENDPOINT.ACTION.EMAIL_SETUP_JOB.REMOVE_REQ,
          ENDPOINT.ACTION.EMAIL_SETUP_JOB.SEND_TEST_EMAIL,
        ]
      : [
          ENDPOINT.FORM.GET.PROJECT_EMAIL_SETUP,
          ENDPOINT.FORM.SAVE.PROJECT_EMAIL_SETUP,
          ENDPOINT.ACTION.EMAIL_SETUP_PROJECT.REMOVE_ALL,
          ENDPOINT.ACTION.EMAIL_SETUP_PROJECT.SAVE_ATTACH,
          ENDPOINT.ACTION.EMAIL_SETUP_PROJECT.DOWNLOAD_ATTACH,
          ENDPOINT.ACTION.EMAIL_SETUP_PROJECT.REMOVE_ATTACH,
          ENDPOINT.ACTION.EMAIL_SETUP_PROJECT.VIEW_REQ,
          ENDPOINT.ACTION.EMAIL_SETUP_PROJECT.REMOVE_REQ,
          ENDPOINT.ACTION.EMAIL_SETUP_PROJECT.SEND_TEST_EMAIL,
        ];
  }, [type]);

  useEffect(() => {
    api.get(
      endpointGetForm,
      { id },
      {
        handleError: response => onInitError(response),
        handleSuccess: response =>
          dispatch({ type: ACTION.FORM_DATA_INIT, data: response }),
      }
    );
  }, []);

  const handleError = ({ status, data: { fields, message } }, setErrors) => {
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        obj[key] = value;
      }
      setErrors(obj);
    } else {
      BasicDialog({ bodyRender: message || status });
    }
  };

  const onSaveForm = (values, { setSubmitting, setErrors, setFieldValue }) => {
    const params = Normalizer.send(values);
    const hasAttachments = values.attachments && values.attachments.length;

    api.post(
      endpointSaveForm,
      { id, ...params },
      {
        handleError: response => {
          setSubmitting(false);
          handleError(response, setErrors);
        },
        handleSuccess: async response => {
          const data = {
            index: 0,
            files: [...values.attachments],
            errors: new Array(values.attachments.length),
          };

          if (hasAttachments) {
            for (let i = 0; i < values.attachments.length; i++) {
              const record = await sendFile(
                values.attachments[i]['file'],
                data,
                {
                  setFieldValue,
                  setErrors,
                }
              );

              if (record) {
                dispatch({ type: ACTION.ATTACHMENTS_SAVE, data: record });
              } else {
                ++data.index;
              }
            }
          }
          if (!hasAttachments || !data.files.length) {
            dispatch({ type: ACTION.FORM_DATA_UPDATE, data: response });
          }

          setSubmitting(false);

          BasicDialog({
            bodyRender: 'Email Setup has saved successfully',
          });
        },
      }
    );
  };

  const onDeleteAll = setSubmitting => {
    ConfirmDialog({
      bodyRender: `Are you sure you want to delete setup email for this ${type}?`,
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() =>
      api.post(
        endpointRemoveAll,
        { id },
        {
          handleError: response => {
            setSubmitting(false);
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => onCloseForm(),
        }
      )
    );
  };

  const sendFile = (
    file,
    { files, errors, index },
    { setFieldValue, setErrors }
  ) =>
    new Promise(resolve => {
      api.post(
        endpointSaveAttach,
        { id, file },
        {
          handleSuccess: response => {
            files.splice(index, 1);
            errors.splice(index, 1);
            setFieldValue('attachments', files, false);
            resolve(response);
          },
          handleError: ({ data: { message = 'ERROR' } }) => {
            errors[index] = message;
            setErrors({ attachments: errors });
            resolve(null);
          },
        }
      );
    });

  const onTestEmail = () => {
    FormDialog({
      title: 'Send Test Email',
      modeless: false,
      compact: true,
      params: { data: { id }, endpoint: endpointSendTestEmail },
      form: (actions, params) => (
        <SendTestEmailForm actions={actions} params={params} />
      ),
    }).then(
      () => BasicDialog({ bodyRender: 'Email has been sent successfully' }),
      response => ResponseErrorDialog({ response })
    );
  };

  const handleDownloadAttach = attachId => {
    api.download(
      endpointDownloadAttach,
      { id, attachId },
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: response => {
          FileHelper.downloadInit(response.file, response.filename);
        },
      }
    );
  };

  const handleAddFile = (
    files,
    { values, setFieldValue, setFieldTouched, arrayHelpers }
  ) => {
    const index = values.attachments.length ? values.attachments.length - 1 : 0;
    for (let i = 0; i < files.length; ++i) {
      setFieldTouched(`attachments[${index + i}]`, true, false);
      arrayHelpers.push(files[i]);
    }

    setFieldValue(
      'attachSize',
      [...values.attachments, ...files].reduce(
        (sum, a) => sum + Number(a.file.size),
        state.attachments.reduce((sum, a) => sum + Number(a.size), 0)
      )
    );
  };

  const handleRemoveFile = (index, { values, setFieldValue, arrayHelpers }) => {
    arrayHelpers.remove(index);
    setFieldValue(
      'attachSize',
      values.attachments
        .filter((f, i) => i !== index)
        .reduce(
          (sum, a) => sum + Number(a.file.size),
          state.attachments.reduce((sum, a) => sum + Number(a.size), 0)
        )
    );
  };

  const handleRemoveAttach = (attachId, { values, setFieldValue }) => {
    ConfirmDialog({
      bodyRender: 'Are you sure you want to remove this attachment?',
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() => {
      dispatch({ type: ACTION.ATTACHMENTS_DELETE_REQUEST, data: attachId });
      api.post(
        endpointRemoveAttach,
        { id, attachId },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => {
            dispatch({
              type: ACTION.ATTACHMENTS_DELETE_SUCCESS,
              data: attachId,
            });
            setFieldValue(
              'attachSize',
              state.attachments
                .filter(f => f.id !== attachId)
                .reduce(
                  (sum, a) => sum + Number(a.size),
                  values.attachments.reduce(
                    (sum, a) => sum + Number(a.file.size),
                    0
                  )
                )
            );
          },
        }
      );
    });
  };

  const handleAddEmailRequirement = () => {
    FormDialog({
      modeless: false,
      compact: true,
      popupInnerStyles: { marginTop: '50px' },
      params: { id, type },
      form: (actions, params) => (
        <AddSetupEmailRequirementsForm actions={actions} params={params} />
      ),
    }).then(
      response =>
        dispatch({ type: ACTION.REQUIREMENTS_CREATE_SUCCESS, data: response }),
      response => ResponseErrorDialog({ response })
    );
  };

  const handleEditEmailRequirement = reqId => {
    FormDialog({
      modeless: false,
      compact: true,
      popupInnerStyles: { marginTop: '50px' },
      params: { id, type, reqId },
      form: (actions, params) => (
        <AddSetupEmailRequirementsForm actions={actions} params={params} />
      ),
    }).then(
      response =>
        dispatch({ type: ACTION.REQUIREMENTS_UPDATE_SUCCESS, data: response }),
      response => ResponseErrorDialog({ response })
    );
  };

  const handleRemoveEmailRequirement = reqId => {
    ConfirmDialog({
      bodyRender: 'Are you sure you want to delete this requirement?',
      dismissWhenClose: true,
      textSuccessBtn: 'Yes',
      textCancelBtn: 'No',
    }).then(() => {
      dispatch({ type: ACTION.REQUIREMENTS_REQUEST, data: reqId });
      api.post(
        endpointRemoveReq,
        { id, reqId },
        {
          handleError: response => {
            dispatch({ type: ACTION.REQUIREMENTS_REQUEST_FAIL, data: reqId });
            ResponseErrorDialog({ response });
          },
          handleSuccess: () =>
            dispatch({ type: ACTION.REQUIREMENTS_REMOVE_SUCCESS, data: reqId }),
        }
      );
    });
  };

  const handleViewEmailRequirement = reqId => {
    api.get(
      endpointViewReq,
      { id, reqId },
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: response =>
          BasicDialog({
            bodyRender: (
              <div className="setup-email__view-requirements">
                <CriteriaList records={response} />
              </div>
            ),
            bodyStyles: {
              width: '100%',
              maxWidth: '480px',
            },
            popupStyles: { maxWidth: '480px', width: '100%' },
          }),
      }
    );
  };

  const handleRecruiterIdChange = (field, value, { values, setFieldValue }) => {
    setFieldValue(field, value);
    setFieldValue(
      'Active',
      hasActiveReq &&
        values.Subject &&
        values.Message.getCurrentContent().hasText() &&
        value
        ? values.Active
        : false
    );
  };

  const handleSubjectChange = (e, { values, handleChange, setFieldValue }) => {
    const {
      target: { value },
    } = e;
    handleChange(e);
    setFieldValue(
      'Active',
      hasActiveReq &&
        value &&
        values.Message.getCurrentContent().hasText() &&
        values.RecruiterID
        ? values.Active
        : false
    );
  };

  const handleMessageChange = (field, value, { values, setFieldValue }) => {
    setFieldValue(field, value);
    setFieldValue(
      'Active',
      hasActiveReq &&
        values.Subject &&
        value.getCurrentContent().hasText() &&
        values.RecruiterID
        ? values.Active
        : false
    );
  };

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <Formik
        initialValues={state.fields}
        onSubmit={onSaveForm}
        enableReinitialize
        validationSchema={SetupEmailFormValidationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          setSubmitting,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="form__body"
            style={{ position: 'relative' }}
          >
            {isSubmitting && <LoadMask relative />}
            <div className="form__inner">
              <PanelContainer styles={{ padding: '0' }}>
                <div
                  className="form__header form__header--with-toggle"
                  style={{ display: 'flex' }}
                >
                  <h4 className="form__title">{state.PositionTitle}</h4>
                  <ActiveSlideToggle
                    name="Active"
                    text={hint}
                    values={values}
                    hasActiveReq={hasActiveReq}
                    onChange={setFieldValue}
                  />
                </div>

                <div
                  className="form__group"
                  style={{ marginBottom: '0', padding: '5px 30px' }}
                >
                  <ErrorMessage
                    name="Active"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div
                  className="form__group form__group--limited"
                  style={{ marginBottom: '15px' }}
                >
                  <BasicLabel
                    name="assigned"
                    required={true}
                    text="Assign To"
                    styles={{
                      fontWeight: 'bold',
                    }}
                  />
                  <BasicSelect
                    name="RecruiterID"
                    value={values.RecruiterID}
                    required={true}
                    placeholder="Start Typing"
                    options={state.options.RecruiterID}
                    onChange={(field, value) =>
                      handleRecruiterIdChange(field, value, {
                        values,
                        setFieldValue,
                      })
                    }
                    onBlur={setFieldTouched}
                    isValid={
                      !(errors.RecruiterID && !touched.RecruiterID) ||
                      !touched.RecruiterID
                    }
                  />

                  <ErrorMessage
                    name="RecruiterID"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              </PanelContainer>

              <PanelContainer styles={{ padding: '0' }}>
                <div className="form__group" style={{ marginBottom: '10px' }}>
                  <EmailRequirements
                    data={state.requirements}
                    actions={{
                      onAdd: handleAddEmailRequirement,
                      onEdit: handleEditEmailRequirement,
                      onRemove: handleRemoveEmailRequirement,
                      onView: handleViewEmailRequirement,
                    }}
                  />
                </div>
              </PanelContainer>

              <PanelContainer styles={{ padding: '0 0 15px 0' }}>
                <div
                  className="form__group form__group--limited"
                  style={{ marginBottom: '5px' }}
                >
                  <h5 className="form__subtitle">Email Template</h5>
                </div>

                <div
                  className="form__group form__group--limited"
                  style={{ marginBottom: '15px' }}
                >
                  <BasicInput
                    label="Subject"
                    required={true}
                    name="Subject"
                    inputType="text"
                    content={values.Subject}
                    placeholder="0 characters of the 100 character limit"
                    controlFunc={e =>
                      handleSubjectChange(e, {
                        values,
                        handleChange,
                        setFieldValue,
                      })
                    }
                    isValid={
                      (!errors.Subject && touched.Subject) || !touched.Subject
                    }
                    touchFunc={handleBlur}
                    labelStyles={{ fontWeight: 'bold' }}
                  />

                  <ErrorMessage
                    name="Subject"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group " style={{ marginBottom: '5px' }}>
                  <BasicLabel
                    name="Message"
                    required={true}
                    text="Message"
                    styles={{
                      fontWeight: 'bold',
                      marginTop: '15px',
                    }}
                  />

                  <TextEditor
                    name="Message"
                    enableHyperlinks
                    editorState={values.Message}
                    controlFunc={(field, value) =>
                      handleMessageChange(field, value, {
                        values,
                        setFieldValue,
                      })
                    }
                    touchFunc={setFieldTouched}
                    isValid={
                      (!errors.Message && !!touched.Message) || !touched.Message
                    }
                  />

                  <ErrorMessage
                    name="Message"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              </PanelContainer>

              <PanelContainer styles={{ padding: '0' }}>
                <div className="form__group" style={{ marginBottom: '5px' }}>
                  <EmailAttachments
                    name="attachments"
                    values={values.attachments}
                    attachments={state.attachments}
                    touched={touched.attachments}
                    errors={errors.attachments}
                    required={false}
                    showDescription={false}
                    onAdd={(files, arrayHelpers) =>
                      handleAddFile(files, {
                        values,
                        setFieldValue,
                        setFieldTouched,
                        arrayHelpers,
                      })
                    }
                    onRemoveAttach={id =>
                      handleRemoveAttach(id, { values, setFieldValue })
                    }
                    onRemove={(index, arrayHelpers) =>
                      handleRemoveFile(index, {
                        values,
                        setFieldValue,
                        arrayHelpers,
                      })
                    }
                    onDownloadAttach={handleDownloadAttach}
                  />
                </div>
              </PanelContainer>

              <PanelContainer styles={{ padding: '10px 0 0 0' }}>
                {hasActiveReq &&
                  values.Message.getCurrentContent().hasText() &&
                  values.RecruiterID &&
                  !values.Active && (
                    <div
                      className="form__group"
                      style={{ marginBottom: '5px', padding: '10px 30px' }}
                    >
                      <div className="setup-email__disclaimer">
                        <div className="setup-email__disclaimer-icon">
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            size={'1x'}
                            className="setup-email__disclaimer-icon"
                          />
                        </div>

                        <p className="setup-email__disclaimer-text">
                          Setup email is Inactive. Activation toggle is located
                          at the top of the page.
                        </p>
                      </div>
                    </div>
                  )}

                <div className="form__controls" style={{ marginTop: '0' }}>
                  <BasicButtonGroup>
                    <BasicSubmitButton
                      disabled={isSubmitting}
                      controlFunc={handleSubmit}
                      btnWidth={150}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={true}
                      btnTextColor="#27282d"
                      btnText="Save"
                    />
                    <BasicButton
                      disabled={!idDeletable}
                      controlFunc={() => onDeleteAll(setSubmitting)}
                      btnWidth={130}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={false}
                      btnTextColor="#27282d"
                      btnText="Delete"
                    />
                    <BasicButton
                      disabled={!testMailEnabled}
                      controlFunc={onTestEmail}
                      btnWidth={130}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnFontSize="0.8571rem"
                      btnArrow={false}
                      btnTextColor="#27282d"
                      btnText="Send test email"
                    />
                    <button
                      type="button"
                      className="form__close-btn"
                      onClick={onCloseForm}
                    >
                      Close
                    </button>
                  </BasicButtonGroup>
                </div>
              </PanelContainer>
            </div>
          </form>
        )}
      </Formik>
    </CSSTransition>
  ) : (
    <FormContentLoader />
  );
};

SetupEmailForm.propTypes = {
  onCloseForm: PropTypes.func.isRequired,
  onInitError: PropTypes.func.isRequired,
};

export default SetupEmailForm;
